import { Component, OnInit, AfterViewInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { ACCESS_LEVEL_LABELS, CONFIG_SWITCHER_MIN_LEVEL, getAccessLevelName as getAccessLevelNameFn, isSuperAdmin, userLevelAtLeast } from '../../constants/access-levels';
import { ConfigListEntry } from '../../shared/app-config.model';
import { ConfigService } from '../../services/config.service';
import { expandConfigKeysByCompanyKey, parseAllowedClientConfigKeysFromUser } from '../../utils/config-list-expand';
import { filterProductBrandsForSalesperson, showSalespersonBrandScope } from '../../utils/salesperson-brand-scope';
import { UsersService } from '../../services/users.service';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlobalsService } from '../../services/globals.service';
import { InventoryService } from '../../services/inventory.service';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';

@Component({
	selector: 'app-user-create',
	templateUrl: './user-create.component.html',
	styleUrls: ['./user-create.component.scss']
})
export class UserCreateComponent implements OnInit, AfterViewInit {
	title = 'New User';
	user: any = {};
	users: any = [];
	userForm: UntypedFormGroup;
	systemForm: UntypedFormGroup;
	salesForm: UntypedFormGroup;
	userdetails: any = [];
	locations: any = [];
	userlocations: any = [];
	usergroups: any = [];
	managers: any[] = [];
	brands: { manufacturer_id: number; name: string }[] = [];
	zones: any = [];
	yesno: any = [
		{ value: '0', name: 'No' },
		{ value: '1', name: 'Yes' }
	];

	grouplevels: any[] = [
		{ name: 'Default', value: '0' },
		{ name: 'Salesman', value: '1' },
		{ name: 'Sales Manager', value: '2' },
		{ name: 'Regional Manager', value: '3' },
	];

	accessLevels = ACCESS_LEVEL_LABELS;

	formEditAllowed = [
		'userid',
		'password',
		'realname',
		'phone',
		'email',
		'defaultlocation',
		'fullaccess',
		'cancreatetender',
		'editinv',
		'dispatchadmin',
		'customeradmin',
		'user_group',
		'empcode',
		'salesman'
		//'phoneno',
	]

	userid: any = '';
	color: any = 'blue';
	defaultlocation: any;
	recs: any = [];
	activeStep: any;
	emailRegex = '^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$';
	phoneRegex = '^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}$';
	empcodes: any = false;
	existingUserids = new Set<string>();
	useridTakenSuggest = '';
	hidePassword = true;
	private lastSuggestedUserid = '';
	allowedUiConfigOptions: ConfigListEntry[] = [];
	accountType: 'internal' | 'salesperson' = 'internal';
	salespersonBrands: {
		manufacturer_id: number;
		brand_name: string;
		supplier_id: string;
		supplier_name: string;
		existing_salesmancode?: string;
		existing_userid?: string;
	}[] = [];
	selectedSalespersonManufacturerId: number | null = null;
	private salesRepUserGroupId: number | null = null;

	constructor(private route: ActivatedRoute, private formBuilder: UntypedFormBuilder, private userService: UsersService, public router: Router, private location: Location, public globalSearchService: GlobalSearchService, private globalsService: GlobalsService, private inventoryService: InventoryService, private configService: ConfigService) {
		this.color = this.globalSearchService.getColor();
	}

	get isMoovSetup(): boolean {
		const client = this.configService.getConfig()?.env?.client;
		return client === 'moov' || client === 'moov_sandbox';
	}

	get isSalespersonSetupMode(): boolean {
		return this.isMoovSetup && this.accountType === 'salesperson';
	}

	get selectedSalespersonBrand() {
		if (this.selectedSalespersonManufacturerId == null) { return null; }
		return this.salespersonBrands.find((b) => b.manufacturer_id === this.selectedSalespersonManufacturerId) || null;
	}

	onAccountTypeChange(): void {
		if (this.isSalespersonSetupMode) {
			this.applySalespersonDefaults();
		} else {
			this.selectedSalespersonManufacturerId = null;
		}
	}

	onSalespersonBrandChange(manufacturerId: number | null): void {
		this.selectedSalespersonManufacturerId = manufacturerId;
		const brand = this.selectedSalespersonBrand;
		if (!brand) { return; }

		const displayName = brand.brand_name || brand.supplier_name;
		this.userForm.patchValue({ realname: displayName }, { emitEvent: true });

		const baseUserid = this.suggestSalespersonUserid(displayName);
		if (baseUserid) {
			this.lastSuggestedUserid = baseUserid;
			this.userForm.get('userid')?.setValue(baseUserid, { emitEvent: false });
			this.useridTakenSuggest = '';
			this.userForm.get('userid')?.updateValueAndValidity();
		}

		const code = this.suggestSalespersonCode(brand);
		this.systemForm.patchValue({
			isSalesman: true,
			salesman: code,
			fullaccess: '5',
			user_group: this.salesRepUserGroupId ?? this.systemForm.get('user_group')?.value,
			customeradmin: '0',
			cancreatetender: '0',
			editinv: '0',
			dispatchadmin: '0',
		}, { emitEvent: true });

		this.salesForm.patchValue({
			salesmancode: code,
			salesmanname: displayName,
			group_level: '2',
			current: '1',
			portal_manufacturer_ids: [brand.manufacturer_id],
			portal_default_manufacturer_id: brand.manufacturer_id,
		}, { emitEvent: false });
	}

	private applySalespersonDefaults(): void {
		this.systemForm.patchValue({
			isSalesman: true,
			isPOSUser: false,
			fullaccess: '5',
			customeradmin: '0',
			cancreatetender: '0',
			editinv: '0',
			dispatchadmin: '0',
			viewcost: '0',
		}, { emitEvent: false });

		if (this.salesRepUserGroupId != null) {
			this.systemForm.get('user_group')?.setValue(this.salesRepUserGroupId);
		}

		this.salesForm.patchValue({
			group_level: '2',
			current: '1',
			access_level: '0',
		}, { emitEvent: false });
	}

	private suggestSalespersonUserid(displayName: string): string {
		const compact = displayName.toLowerCase().replace(/[^a-z0-9]+/g, '');
		if (compact) {
			return this.firstAvailableUserid(compact).toLowerCase();
		}
		return '';
	}

	private suggestSalespersonCode(brand: { brand_name?: string; supplier_id?: string }): string {
		const supplierId = (brand.supplier_id || '').trim().toUpperCase();
		if (supplierId && supplierId.length <= 6 && /^[A-Z0-9]+$/.test(supplierId)) {
			return supplierId;
		}
		const words = (brand.brand_name || '').toUpperCase().split(/\s+/).filter((w) => w && !['LLC', 'INC', 'THE', 'CO', 'COMPANY', 'CANNABIS', 'TREATS'].includes(w));
		if (words.length >= 2) {
			return (words[0].charAt(0) + words[1].substring(0, 2)).replace(/[^A-Z0-9]/g, '');
		}
		if (words.length === 1) {
			return words[0].substring(0, 3).replace(/[^A-Z0-9]/g, '');
		}
		return supplierId.substring(0, 3);
	}

	private loadSalespersonBrands(): void {
		this.userService.getSalespersonBrands().pipe(take(1)).subscribe((rows: any) => {
			this.salespersonBrands = (rows || []).map((r: any) => ({
				manufacturer_id: Number(r.manufacturer_id),
				brand_name: r.brand_name || r.brand || '',
				supplier_id: r.supplier_id || r.supplierno || '',
				supplier_name: r.supplier_name || r.suppname || '',
				existing_salesmancode: r.existing_salesmancode || '',
				existing_userid: r.existing_userid || '',
			})).filter((b) => b.manufacturer_id > 0 && !b.existing_userid);
		});
	}

	get showSalespersonBrandScope(): boolean {
		return showSalespersonBrandScope(this.configService.getConfig());
	}

	portalBrandLabel(manufacturerId: number): string {
		const row = this.brands.find((b) => b.manufacturer_id === manufacturerId);
		return row ? row.name : String(manufacturerId);
	}

	onPortalBrandsChange(): void {
		if (!this.salesForm) { return; }
		const ids = (this.salesForm.get('portal_manufacturer_ids')?.value || []) as number[];
		const defCtrl = this.salesForm.get('portal_default_manufacturer_id');
		const cur = defCtrl?.value != null && defCtrl.value !== '' ? Number(defCtrl.value) : null;
		if (cur != null && Number.isFinite(cur) && !ids.includes(cur)) {
			defCtrl?.setValue(ids.length ? ids[0] : null, { emitEvent: false });
		}
		if (!ids.length) {
			defCtrl?.setValue(null, { emitEvent: false });
		}
	}

	get canEditAllowedClientConfigs(): boolean {
		return userLevelAtLeast(this.globalSearchService.user.getValue(), CONFIG_SWITCHER_MIN_LEVEL);
	}

	private loadAllowedUiConfigOptions(): void {
		this.configService.loadAvailableConfigs().pipe(take(1)).subscribe(list => {
			const base = list || [];
			const editor = this.globalSearchService.user.getValue();
			if (!userLevelAtLeast(editor, CONFIG_SWITCHER_MIN_LEVEL)) {
				this.allowedUiConfigOptions = [];
				return;
			}
			const editorKeys = parseAllowedClientConfigKeysFromUser(editor?.user);
			if (!editorKeys.length) {
				this.allowedUiConfigOptions = isSuperAdmin(editor) ? base : [];
				return;
			}
			const expanded = expandConfigKeysByCompanyKey(editorKeys, base);
			const allow = new Set(expanded);
			this.allowedUiConfigOptions = base.filter((c) => allow.has(c.config));
		});
	}

	ngOnInit(): void {
		this.userForm = this.formBuilder.group({
			userid: ['', [Validators.required, Validators.pattern('[-_a-zA-Z0-9]*')]],
			password: ['', Validators.required],
			realname: ['', Validators.required],
			phone: ['', Validators.pattern(this.phoneRegex)],
			email: ['', Validators.pattern(this.emailRegex)],
		});

		this.systemForm = this.formBuilder.group({
			defaultlocation: ['', Validators.required],
			fullaccess: ['1', Validators.required],
			empcode: [''],
			salesman: [''],
			user_group: ['', Validators.required],
			customeradmin: ['1'],
			cancreatetender: ['1'],
			editinv: ['1'],
			dispatchadmin: ['0'],
			viewcost: ['0'],
			department: [''],
			isPOSUser: [false],
			isSalesman: [false],
			allowedUiConfigs: [[]],
		});

		this.salesForm = this.formBuilder.group({
			salesmancode: [''],
			salesmanname: [''],
			smantel: [''],
			smanfax: [''],
			commissionrate1: [''],
			commissionrate2: [''],
			breakpoint: [''],
			current: ['1'],
			chatid: [''],
			group_id: ['1'],
			group_level: ['0'],
			group_type: ['1'],
			username: [''],
			smanlogin: [''],
			smanpass: [''],
			smanaddr: [''],
			smancity: [''],
			smanstate: [''],
			smanzip: [''],
			smantab: [''],
			smancell: [''],
			smanemail: [''],
			access_level: ['0'],
			csremails: [''],
			commlock: [0],
			manager: [''],
			assinged_salespeople: [[]],
			portal_manufacturer_ids: [[]],
			portal_default_manufacturer_id: [null],
		});

		this.userForm.markAllAsTouched();
		this.systemForm.markAllAsTouched();
		this.user['locations'] = [];

		if (!this.canEditAllowedClientConfigs) {
			this.systemForm.get('allowedUiConfigs')?.disable();
		}

		this.getEmployeeCodes();

		this.systemForm.get('isPOSUser')?.valueChanges.subscribe(enabled => {
			const ctrl = this.systemForm.get('empcode');
			if (enabled) {
				ctrl?.setValidators([Validators.required]);
			} else {
				ctrl?.clearValidators();
				ctrl?.reset();
			}
			ctrl?.updateValueAndValidity();
			ctrl?.markAsTouched();
		});

		this.systemForm.get('isSalesman')?.valueChanges.subscribe(enabled => {
			const emailUser = this.userForm.get('email');

			if (enabled) {
				this.systemForm.get('salesman')?.setValidators([Validators.required]);
				// Pre-fill salesForm name from realname and email
				const realname = this.userForm.get('realname')?.value || '';
				const email = emailUser?.value || '';
				if (realname && !this.salesForm.get('salesmanname')?.value) {
					this.salesForm.get('salesmanname')?.setValue(realname);
				}
				if (email && !this.salesForm.get('smanemail')?.value) {
					this.salesForm.get('smanemail')?.setValue(email);
				}
			} else {
				this.systemForm.get('salesman')?.clearValidators();
				emailUser?.setValidators([Validators.pattern(this.emailRegex)]);
				this.salesForm.reset({
					current: '1', group_id: '1', group_level: '0',
					group_type: '1', access_level: '0', commlock: 0,
					portal_manufacturer_ids: [], portal_default_manufacturer_id: null,
					assinged_salespeople: [],
				});
			}

			this.systemForm.get('salesman')?.updateValueAndValidity();
			this.systemForm.get('salesman')?.markAsTouched();
			emailUser?.updateValueAndValidity();
			emailUser?.markAsTouched();
		});

		// Keep salesmancode in sync with salesman code field
		this.systemForm.get('salesman')?.valueChanges.subscribe(val => {
			if (this.systemForm.get('isSalesman')?.value) {
				this.salesForm.get('salesmancode')?.setValue(val, { emitEvent: false });
			}
		});

		this.systemForm.get('empcode').valueChanges.subscribe((newV) => {
			if (Object.keys(this.empcodes).includes(newV) && newV !== '') {
				this.systemForm.get('empcode').setErrors({ 'error': true });
			} else {
				this.systemForm.get('empcode').setErrors(null);
				this.systemForm.updateValueAndValidity();
			}
		});

		this.userForm.get('realname')?.valueChanges.subscribe((name) => {
			const useridVal = (this.userForm.get('userid')?.value || '').trim();
			if (!useridVal || useridVal === this.lastSuggestedUserid) {
				const base = this.baseUseridFromName(name);
				if (base) {
					const suggested = this.firstAvailableUserid(base).toUpperCase();
					this.lastSuggestedUserid = suggested;
					this.userForm.get('userid')?.setValue(suggested, { emitEvent: false });
					this.useridTakenSuggest = '';
					this.userForm.get('userid')?.updateValueAndValidity();
				}
			}
		});

		this.userForm.get('userid')?.valueChanges.subscribe(() => {
			this.validateUseridTaken();
		});
	}

	loading = false;

	getEmployeeCodes() {
		this.userService.getUsers().subscribe((res: any) => {
			const codes: Record<string, string> = {};
			this.existingUserids.clear();
			(res || []).forEach((emp: any) => {
				if (emp.empcode) codes[emp.empcode] = emp.userid;
				if (emp.userid) this.existingUserids.add((emp.userid as string).toLowerCase());
			});
			this.empcodes = codes;
			this.validateUseridTaken();
		});
	}

	private baseUseridFromName(fullName: string): string {
		if (!fullName || typeof fullName !== 'string') return '';
		const words = fullName.trim().split(/\s+/).filter(w => w.length > 0);
		if (words.length === 0) return '';
		if (words.length === 1) return words[0].toLowerCase().replace(/[^a-z0-9_-]/g, '');
		const first = words[0];
		const last = words[words.length - 1];
		const base = (first.charAt(0) + last).toLowerCase().replace(/[^a-z0-9_-]/g, '');
		return base || words[0].toLowerCase().replace(/[^a-z0-9_-]/g, '');
	}

	private firstAvailableUserid(base: string): string {
		if (!base) return '';
		const lower = base.toLowerCase();
		if (!this.existingUserids.has(lower)) return base;
		for (let n = 2; n <= 999; n++) {
			const candidate = lower + n;
			if (!this.existingUserids.has(candidate)) return candidate;
		}
		return lower + Date.now().toString(36);
	}

	generatePassword(): void {
		const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
		let pass = '';
		for (let i = 0; i < 6; i++) {
			pass += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		this.userForm.get('password')?.setValue(pass);
	}

	suggestUseridFromName(): void {
		const realname = this.userForm.get('realname')?.value;
		const base = this.baseUseridFromName(realname);
		if (!base) return;
		const suggested = this.firstAvailableUserid(base).toUpperCase();
		this.lastSuggestedUserid = suggested;
		this.userForm.get('userid')?.setValue(suggested);
		this.useridTakenSuggest = '';
		this.userForm.get('userid')?.updateValueAndValidity();
	}

	private validateUseridTaken(): void {
		const ctrl = this.userForm.get('userid');
		const val = (ctrl?.value || '').trim();
		if (!val) {
			this.useridTakenSuggest = '';
			ctrl?.setErrors(ctrl?.errors ? { ...ctrl.errors } : null);
			return;
		}
		const lower = val.toLowerCase();
		if (this.existingUserids.has(lower)) {
			const base = this.baseUseridFromName(this.userForm.get('realname')?.value) || lower.replace(/\d+$/, '') || lower;
			this.useridTakenSuggest = this.firstAvailableUserid(base).toUpperCase();
			ctrl?.setErrors({ ...(ctrl.errors || {}), taken: true });
		} else {
			this.useridTakenSuggest = '';
			const errs = ctrl?.errors;
			if (errs && errs['taken']) {
				const { taken, ...rest } = errs;
				ctrl?.setErrors(Object.keys(rest).length ? rest : null);
			}
		}
		ctrl?.updateValueAndValidity();
	}

	createUser() {
		if (this.isSalespersonSetupMode) {
			this.createSalespersonUser();
			return;
		}

		if (this.userForm.valid && this.systemForm.valid && !this.loading) {
			this.loading = true;

			const isSalesman = !!this.systemForm.get('isSalesman')?.value;
			const salesRaw = this.salesForm.getRawValue();

			const formData: any = {
				...this.userForm.value,
				...this.systemForm.getRawValue(),
				// Include core salesman fields so backend can create the salesman record
				...(isSalesman ? {
					salesmanname: salesRaw.salesmanname || this.userForm.get('realname')?.value || '',
					smantel: salesRaw.smantel,
					smanfax: salesRaw.smanfax,
					commissionrate1: salesRaw.commissionrate1,
					commissionrate2: salesRaw.commissionrate2,
					breakpoint: salesRaw.breakpoint,
					current: salesRaw.current,
					chatid: salesRaw.chatid,
					group_id: salesRaw.group_id,
					group_level: salesRaw.group_level,
					group_type: salesRaw.group_type,
					username: salesRaw.username || this.userForm.get('userid')?.value || '',
					smanlogin: salesRaw.smanlogin || this.userForm.get('userid')?.value || '',
					smanpass: salesRaw.smanpass,
					smanaddr: salesRaw.smanaddr,
					smancity: salesRaw.smancity,
					smanstate: salesRaw.smanstate,
					smanzip: salesRaw.smanzip,
					smantab: salesRaw.smantab,
					smancell: salesRaw.smancell,
					smanemail: salesRaw.smanemail || this.userForm.get('email')?.value || '',
					access_level: salesRaw.access_level,
					csremails: salesRaw.csremails,
					commlock: salesRaw.commlock,
					manager: salesRaw.manager,
					portal_manufacturer_ids: Array.isArray(salesRaw.portal_manufacturer_ids)
						? salesRaw.portal_manufacturer_ids.map((n: any) => Number(n)).filter((n: number) => n > 0)
						: [],
					portal_default_manufacturer_id: salesRaw.portal_default_manufacturer_id,
				} : {}),
			};

			const keys = formData.allowedUiConfigs as string[] | undefined;
			delete formData.allowedUiConfigs;
			if (this.canEditAllowedClientConfigs) {
				formData.allowed_client_configs = keys && keys.length ? JSON.stringify(keys) : null;
			}
			// Remove UI-only toggles from payload
			delete formData.isPOSUser;
			delete formData.isSalesman;

			this.userService.createUser(this.userForm.get('userid').value, formData).subscribe({
				next: (results) => {
					this.user.user = results;
					this.updateLocations();

					// After user created, persist full salesman details (incl. portal brands) via saveSalesPerson
					if (isSalesman && salesRaw.salesmancode) {
						const pids = Array.isArray(salesRaw.portal_manufacturer_ids)
							? salesRaw.portal_manufacturer_ids.map((n: any) => Number(n)).filter((n: number) => n > 0)
							: [];
						const pdf = salesRaw.portal_default_manufacturer_id != null && salesRaw.portal_default_manufacturer_id !== ''
							? Number(salesRaw.portal_default_manufacturer_id) : null;
						const salesPayload = {
							...salesRaw,
							portal_manufacturer_ids: pids,
							portal_default_manufacturer_id: pdf != null && Number.isFinite(pdf) && pids.includes(pdf) ? pdf : pids.length ? pids[0] : null,
						};
						this.globalsService.saveSalesPerson(salesPayload).subscribe({
							next: () => {
								this.globalSearchService.showNotification('User created', 'success', 'bottom', 'right');
								this.location.back();
							},
							error: () => {
								this.globalSearchService.showNotification('User created but salesperson details could not be saved', 'warning', 'bottom', 'right');
								this.location.back();
							},
						});
					} else {
						this.globalSearchService.showNotification('User created', 'success', 'bottom', 'right');
						this.location.back();
					}
				},
				error: (err) => {
					console.error('Create user failed:', err);
					this.globalSearchService.showNotification('Failed to create user', 'danger', 'bottom', 'right');
				},
				complete: () => {
					this.loading = false;
				}
			});
		}
	}

	private createSalespersonUser(): void {
		const brand = this.selectedSalespersonBrand;
		if (!brand || !this.userForm.valid || !this.systemForm.valid || this.user.locations.length < 1 || this.loading) {
			this.userForm.markAllAsTouched();
			this.systemForm.markAllAsTouched();
			if (!brand) {
				this.globalSearchService.showNotification('Select a brand', 'warning', 'bottom', 'right');
			}
			return;
		}

		this.loading = true;
		const payload = {
			manufacturer_id: brand.manufacturer_id,
			userid: String(this.userForm.get('userid')?.value || '').trim().toLowerCase(),
			password: this.userForm.get('password')?.value,
			realname: this.userForm.get('realname')?.value,
			email: this.userForm.get('email')?.value || '',
			phone: this.userForm.get('phone')?.value || '',
			salesmancode: String(this.systemForm.get('salesman')?.value || '').trim().toUpperCase(),
			defaultlocation: this.systemForm.get('defaultlocation')?.value,
			user_group: this.systemForm.get('user_group')?.value,
		};

		this.userService.createSalesperson(payload).subscribe({
			next: (result: any) => {
				if (!result?.success) {
					this.globalSearchService.showNotification(result?.message || 'Failed to create salesperson login', 'danger', 'bottom', 'right');
					this.loading = false;
					return;
				}
				this.userid = payload.userid;
				this.updateLocations();
				this.globalSearchService.showNotification(
					`Salesperson login created (${result.salesmancode})`,
					'success',
					'bottom',
					'right'
				);
				this.loading = false;
				this.location.back();
			},
			error: (err) => {
				console.error('Create salesperson failed:', err);
				this.globalSearchService.showNotification('Failed to create salesperson login', 'danger', 'bottom', 'right');
				this.loading = false;
			},
		});
	}

	ngAfterViewInit() {
		this.loadAllowedUiConfigOptions();

		this.globalsService.getLocations().subscribe(details => {
			this.locations = details || [];
			if (this.locations.length === 1) {
				const loc = this.locations[0];
				this.user.locations.push(loc);
				this.locations = [];
				this.defaultlocation = loc;
				this.systemForm.get('defaultlocation')?.setValue(loc.loccode);
				this.sortlocations();
			}
		});

		this.globalsService.getAllLocations().subscribe(details => {
			this.recs = details || [];
		});

		this.userService.getUserGroups().subscribe((groups: any) => {
			this.usergroups = Array.isArray(groups) ? groups : [];
			const salesRep = this.usergroups.find((g: any) => String(g?.name || '').toUpperCase() === 'SALES REP');
			if (salesRep?.user_group_id != null) {
				this.salesRepUserGroupId = Number(salesRep.user_group_id);
			}
		});

		if (this.isMoovSetup) {
			this.loadSalespersonBrands();
		}

		this.globalsService.getSalesManagers().subscribe((results: any) => {
			this.managers = results || [];
		});

		this.globalSearchService.zones.subscribe((results: any) => {
			this.zones = results;
		});

		if (this.showSalespersonBrandScope) {
			this.inventoryService.getItemManufacturer().subscribe((rows: any) => {
				this.brands = filterProductBrandsForSalesperson(rows);
			});
		}
	}

	updateLocations() {

		const data = {
			userid: this.userid == '' ? this.userForm.get('userid').value : this.userid,
			locations: this.user.locations
		};


		this.userService.updateUserLocations(data).subscribe((user: any) => {
			this.user = user;
		});
	}

	addUserLocation(loc: any) {
		const index = this.locations.indexOf(loc)
		this.locations.splice(index, 1);
		this.user.locations.push(loc)

		this.sortlocations();
	}

	removeUserLocation(loc: any) {

		if (loc == this.defaultlocation) {
			alert('Can not remove default location from the list.');
			return;
		}
		const index = this.user.locations.indexOf(loc)
		this.user.locations.splice(index, 1);
		this.locations.push(loc)
		this.sortlocations();

	}

	sortlocations() {
		this.locations.sort((n1, n2) => n1.loccode - n2.loccode);
		this.user.locations.sort((n1, n2) => n1.loccode - n2.loccode);
	}

	defaultLocation(event: any): void {
		if (!event?.value || !Array.isArray(this.locations) || !Array.isArray(this.user?.locations)) {return;}
	
		const newLocCode = event.value;
	
		// Find the new default location (from either list)
		const newLocation = this.locations.find(loc => loc.loccode === newLocCode)
			|| this.user.locations.find(loc => loc.loccode === newLocCode);
	
		if (!newLocation || (this.defaultlocation?.loccode === newLocCode)) {return;}
	
		// Move previous default back to location pool if it exists
		if (this.defaultlocation) {
			this.locations.push(this.defaultlocation);
			this.user.locations = this.user.locations.filter(loc => loc.loccode !== this.defaultlocation.loccode);
		}
	
		// Remove new location from existing pools to avoid duplication
		this.locations = this.locations.filter(loc => loc.loccode !== newLocCode);
		this.user.locations = this.user.locations.filter(loc => loc.loccode !== newLocCode);
	
		// Set new default and push to user locations
		this.defaultlocation = newLocation;
		this.user.locations.push(newLocation);
	
		this.sortlocations();
	}

	back(): void {
		this.location.back()
	}

	translate_yn(input: any) {
		if (input == 0 || input == 1) {
			return input == 0 ? 'No' : 'Yes';
		} else {
			return '?';
		}
	}

	getAccessLevelName(value: string): string {
		return getAccessLevelNameFn(value);
	}

	translate_usergroup(input: any) {
		if (input != '') {
			return this.usergroups.filter(group => (group['user_group_id'] == input))[0]['name'];
		} else {
			return '';
		}
	}

	logStep(event: any) {
		this.activeStep = event.selectedIndex;
	}

	format_phone(input: HTMLInputElement): void {
		if (!input || typeof input.value !== 'string') {return;}
	
		const digits = input.value.replace(/\D/g, '').slice(0, 10); // limit to 10 digits max
	
		let formatted = '';
	
		if (digits.length >= 6) {
			formatted = `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
		} else if (digits.length >= 3) {
			formatted = `(${digits.slice(0, 3)})${digits.slice(3)}`;
		} else {
			formatted = digits;
		}
	
		input.value = formatted;
	}


}